import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { ResizeHandleState } from '@helios/generic-components'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  ExtFileManagerFilter,
  RootState,
  selectActiveSavedViews,
  selectActualReconDate,
  selectExtFileManagerFilter,
  setExtFileManagerFilter,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { toISOFormat } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'
import {
  ExternalFileManagerKey,
  FileManagerNodeGridKey,
  PositionGridKey,
  TransactionGridKey,
} from '../panel-key'
import { Node, UINode } from './models/nodes.model'
import { Position } from './models/position.model'
import { Transaction } from './models/transaction.model'
import { NodeService } from './services/node/node.service'
import { PositionsService } from './services/positions/positions.service'
import { TransactionService } from './services/transaction/transaction.service'
import { flattenNodes } from './utils/node-utils'

interface ExternalFileManagerPanelState {
  resizeHandleState: ResizeHandleState
}

@Component({
  selector: 'hs-external-file-manager',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './external-file-manager-panel.component.html',
  styleUrls: ['./external-file-manager-panel.component.scss'],
})
export class ExternalFileManagerPanelComponent
  extends BasePanelComponent<ExternalFileManagerPanelState>
  implements OnInit, OnDestroy
{
  activeTab = 1
  uiNodes: UINode[] = []
  panelKey = ExternalFileManagerKey
  private readonly _destroyed$ = new Subject<void>()
  private readonly partitionType = 'ACCOUNT'
  private reconDate: string | null = null
  private filter: ExtFileManagerFilter = {}

  isLoading$ = new BehaviorSubject<boolean>(false)
  accountId$ = new BehaviorSubject<number | null>(null)
  selectedDate$ = new BehaviorSubject<string>('')
  executionNumber$ = new BehaviorSubject<number | null>(null)
  selectedNodes$ = new BehaviorSubject<UINode[]>([])

  savedViewsState$ = this.store
    .select(selectActiveSavedViews)
    .pipe(
      map(savedViews =>
        savedViews?.filter(view =>
          [
            FileManagerNodeGridKey,
            TransactionGridKey,
            PositionGridKey,
          ].includes(view.gridKey),
        ),
      ),
    )

  filter$ = this.store
    .select(selectExtFileManagerFilter)
    .pipe(tap(filter => (this.filter = filter)))

  reconDate$: Observable<string | null> = this.store
    .select(selectActualReconDate)
    .pipe(
      distinctUntilChanged(),
      map(reconDate => {
        if (this.filter.selectedDate) {
          // apply preserved selectedDate instead of recon date
          return this.filter.selectedDate
        }

        if (reconDate == undefined) {
          return null
        }
        const isoDate = toISOFormat(reconDate) as string
        this.selectedDate$.next(isoDate)
        return isoDate
      }),
    )

  positions$: Observable<Position[] | null> = this.executionNumber$.pipe(
    switchMap(executionNumber => {
      if (executionNumber == null) {
        return of(null)
      }
      return this.positionsService.getPositions(executionNumber)
    }),
  )

  transactions$: Observable<Transaction[] | null> = this.executionNumber$.pipe(
    switchMap(executionNumber => {
      if (executionNumber == null) {
        return of(null)
      }
      return this.transactionService.getTransactions(executionNumber)
    }),
  )

  uiNodes$: Observable<UINode[]> = combineLatest([
    this.accountId$,
    this.selectedDate$,
  ]).pipe(
    switchMap(([accountId, selectedDate]) => {
      this.isLoading$.next(true)
      if (accountId != null) {
        return this.nodesService
          .getNodes(
            this.filter.selectedDate ?? selectedDate,
            this.partitionType,
            accountId,
          )
          .pipe(
            catchError(error => {
              // Handle the error (e.g., show a toast message)
              this.toastService.error('Failed to fetch nodes. Error : ' + error)
              return of([]) // Return an empty array or some default value
            }),
          )
      } else {
        return of([])
      }
    }),
    map(nodes => {
      this.isLoading$.next(false)
      return flattenNodes(nodes)
    }),
  )

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly nodesService: NodeService,
    private readonly positionsService: PositionsService,
    private readonly transactionService: TransactionService,
    private readonly toastService: ToastService,
  ) {
    super(container)
  }

  ngOnInit() {
    this.filter$.pipe(takeUntil(this._destroyed$)).subscribe()
    this.accountId$
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe(accountId => {
        if (accountId) {
          this._resetExtFileManagerStateFilter({ accountId })
        }
      })

    this.selectedDate$
      .pipe(takeUntil(this._destroyed$), distinctUntilChanged())
      .subscribe(selectedDate => {
        this._resetExtFileManagerStateFilter({ selectedDate })
      })

    this.reconDate$.pipe(takeUntil(this._destroyed$)).subscribe(reconDate => {
      this.reconDate = toISOFormat(reconDate)
    })

    this.uiNodes$.pipe(takeUntil(this._destroyed$)).subscribe(nodes => {
      this.uiNodes = nodes
      // apply persisted state filter when ext file manager data is available
      if (nodes.length && this.filter.executionNumber) {
        this.executionNumber$.next(this.filter.executionNumber)
        this.activeTab = this.filter.activePanelId ?? this.activeTab
      }
    })
  }

  private _resetExtFileManagerStateFilter(filter: ExtFileManagerFilter) {
    // reset the active tab
    this.activeTab = 1
    // deselect execution when account id is changed
    this.executionNumber$.next(null)

    // reset state filter first and then set accountId
    this.store.dispatch(
      setExtFileManagerFilter({
        ...filter,
        executionNumber: undefined,
        activePanelId: this.activeTab,
      }),
    )
  }

  ngOnDestroy() {
    this.accountId$.complete()
    this.selectedDate$.complete()
    this.executionNumber$.complete()
    this.selectedNodes$.complete()

    this._destroyed$.next()
    this._destroyed$.complete()
  }

  onExecutionNumberChange(newExecutionNumber: number | null) {
    this.executionNumber$.next(newExecutionNumber)
    this.store.dispatch(
      setExtFileManagerFilter({
        executionNumber: newExecutionNumber ?? undefined,
      }),
    )
  }

  onActiveTabChange(activeTab: number) {
    this.activeTab = activeTab
    this.store.dispatch(setExtFileManagerFilter({ activePanelId: activeTab }))
  }

  onActivateEvent(node: Node) {
    this.requestActivate(node)
  }

  onInactivateEvent(node: Node) {
    this.requestInactivate(node)
  }

  private requestActivate(node: Node) {
    const accountId = this.accountId$.value
    if (accountId == null) {
      throw new Error('account id is required')
    }
    const executionId = node.execution?.executionId
    if (executionId == undefined) {
      throw new Error('cannot activate node without execution id')
    }
    const selectedDate = this.selectedDate$.value

    this.toastService.info(
      `Submitting request to activate execution ${executionId} for ${this.partitionType} ${accountId} on ${this.reconDate}`,
      {
        closeButton: true,
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      },
    )

    this.nodesService
      .activate(selectedDate, this.partitionType, accountId, [executionId])
      .pipe(take(1))
      .subscribe()
  }

  private requestInactivate(node: Node) {
    const accountId = this.accountId$.value
    if (accountId == null) {
      throw new Error('account id is required')
    }
    const executionId = node.execution?.executionId
    if (executionId == undefined) {
      throw new Error('cannot inactivate node without execution id')
    }
    const selectedDate = this.selectedDate$.value

    this.toastService.info(
      `Submitting request to inactivate execution ${executionId} for ${this.partitionType} ${accountId} on ${this.reconDate}`,
      {
        closeButton: true,
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      },
    )

    this.nodesService
      .inactivate(selectedDate, this.partitionType, accountId, [executionId])
      .pipe(take(1))
      .subscribe()
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }
}
